import * as Sentry from "@sentry/react";
import { Auth0Provider, type AppState } from "@auth0/auth0-react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router";
import { App } from "./App";
import { captureInviteFromUrl } from "./lib/invite";
import { ThemeProvider } from "./lib/theme";
// Self-hosted brand fonts (OD-6): Inter variable for UI, Space Grotesk 700 for display/wordmark.
import "@fontsource-variable/inter";
import "@fontsource/space-grotesk/700.css";
import "./styles/index.css";

// Errors only (OD-30). VITE_SENTRY_DSN / VITE_ENVIRONMENT are baked in at build time by
// deploy-web.yml; with no DSN the SDK is never initialised. `environment` comes from the deploy
// (dev|prod), NOT import.meta.env.MODE, which is "production" for every vite build and made
// SunshineBids' dev and prod web events indistinguishable (its S229).
// Sentry 11 replaced `sendDefaultPii: false` with `dataCollection`, whose defaults collect
// everything; the PII-bearing categories are turned off explicitly (mirrors apps/api/src/index.ts).
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT ?? "local",
    release: __APP_VERSION__,
    tracesSampleRate: 0,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpBodies: [],
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
    },
  });
}

// Lift an invite token out of the URL before the Auth0 SDK or the router look at it.
captureInviteFromUrl();

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE;
if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID || !AUTH0_AUDIENCE) {
  throw new Error("VITE_AUTH0_DOMAIN / VITE_AUTH0_CLIENT_ID / VITE_AUTH0_AUDIENCE must be set at build time");
}

/** Auth0 inside the router, so the post-login redirect can navigate client-side. */
function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN!}
      clientId={AUTH0_CLIENT_ID!}
      authorizationParams={{ audience: AUTH0_AUDIENCE, redirect_uri: window.location.origin }}
      // Tokens live in memory only (never localStorage). A reload restores the session through
      // the rotating refresh token's fallback: silent auth against auth.hyphesync.com, which is
      // same-site with app./dev. (the custom domain), so third-party-cookie blocking can't break it.
      cacheLocation="memory"
      useRefreshTokens
      useRefreshTokensFallback
      onRedirectCallback={(state?: AppState) => navigate(state?.returnTo ?? "/", { replace: true })}
    >
      {children}
    </Auth0Provider>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing from index.html");
createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  </StrictMode>,
);
